import { Alert, Button, Card, Flex, Tooltip, Typography } from 'antd'
import { ArrowLeft, ChevronDown, History, SquarePen } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import {
  ChatAttachButton,
  ChatAttachmentsHeader,
  hasPendingResolution,
  useStyles,
  type ChatComposerExtras,
  type ChatComposerHandle
} from 'src/components/chat'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import { greetingKey } from 'src/utils'
import { resolveWorkspaceAccessRoleFromSession } from 'src/utils/workspaceMenuAccess'

import {
  AgentGlyph,
  ChatFilesButton,
  ChatHistory,
  isTempId,
  SquadChatConversation,
  useHomeChatFiles,
  useSquadChat
} from '../../squad'

import { ChatWelcome } from './ChatWelcome'
import { resolveSuggestionRole } from './suggestions'

const { Text } = Typography

const AI_ICON_SIZE = 24
const HERO_AVATAR_SIZE = 56

const HEADER_ICON_SIZE = 15

type HistoryPopoverTarget = 'conversation' | 'history' | null

export const WorkspaceHomePage = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const { user } = useSessionContext()
  const welcomeRef = useRef<ChatComposerHandle>(null)

  const workspaceId = useMemo(() => {
    const n = Number(id)

    return Number.isFinite(n) ? n : NaN
  }, [id])

  const idValid = id !== 'new' && Number.isFinite(workspaceId)
  const squad = useSquadChat(idValid ? workspaceId : undefined)
  const {
    glyphAgent,
    configured,
    conversations,
    session,
    appToken,
    historyOpen
  } = squad
  const active = conversations.active

  const awaitingResolution = hasPendingResolution(active)

  const [input, setInput] = useState('')
  const [popoverTarget, setPopoverTarget] = useState<HistoryPopoverTarget>(null)
  const files = useHomeChatFiles({
    agentId: squad.agentId,
    appToken: appToken ?? '',
    send: session.send,
    restoreDraft: setInput
  })
  const attachDisabled =
    session.streaming || !squad.enabled || awaitingResolution

  const greeting = useMemo(
    () => t(`workspaceHome.greeting.${greetingKey()}`),
    [t]
  )

  const suggestionRole = useMemo(
    () =>
      resolveSuggestionRole(
        resolveWorkspaceAccessRoleFromSession(user, workspaceId)
      ),
    [user, workspaceId]
  )

  useEffect(() => {
    const frame = requestAnimationFrame(() => welcomeRef.current?.focus())

    return () => cancelAnimationFrame(frame)
  }, [conversations.activeId])

  const submit = useCallback(
    (prompt: string) => {
      setInput('')
      void session.send(prompt)
    },
    [session]
  )

  const sendComposer = () => {
    if (files.isUploading) return
    setInput('')
    files.submit(input)
  }

  const startNewChat = useCallback(() => {
    session.stop()
    session.dismissError()
    squad.closeHistory()
    conversations.selectConversation(null)
    setInput('')
    files.discardAll()
  }, [conversations, session, squad, files])

  const onPopoverOpenChange = (
    target: Exclude<HistoryPopoverTarget, null>,
    open: boolean
  ) => {
    setPopoverTarget(open ? target : null)
    if (open) squad.openHistory()
    else squad.closeHistory()
  }

  if (!idValid) {
    return <Navigate to={paths.workspaces.root} replace />
  }

  const firstName =
    user?.user?.first_name ?? t('workspaceHome.greeting.fallbackName')

  const composerExtras: ChatComposerExtras = {
    header: files.items.length ? (
      <ChatAttachmentsHeader
        items={files.items}
        onRemove={files.remove}
        onRetry={files.retry}
      />
    ) : undefined,
    prefix: <ChatAttachButton onFiles={files.add} disabled={attachDisabled} />,
    onPasteFiles: attachDisabled ? undefined : files.add,
    allowEmptySend: files.hasReady,
    sendDisabled: files.isUploading
  }

  return (
    <div
      className={cx(styles.page, active ? styles.pageConversation : undefined)}
    >
      <div className={styles.chatMain}>
        {active ? null : (
          <div className={styles.heroTopBar}>
            <ChatHistory squad={squad} />
          </div>
        )}

        {!configured ? (
          <Alert
            type="warning"
            showIcon
            className={styles.configBanner}
            title={t('workspaceHome.errors.notConfiguredTitle')}
            description={
              <Text style={{ fontSize: 12 }}>
                {t('workspaceHome.errors.notConfigured')}
              </Text>
            }
          />
        ) : session.error ? (
          <Alert
            type="error"
            showIcon
            closable={{ onClose: session.dismissError }}
            className={styles.configBanner}
            title={session.error}
          />
        ) : null}

        {active ? (
          <Card
            variant="outlined"
            className={styles.chatCard}
            styles={{
              body: {
                padding: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
          >
            <Flex
              align="center"
              justify="space-between"
              className={styles.conversationHeader}
            >
              <Flex align="center" gap={2} className={styles.topBarLeft}>
                <Tooltip title={t('workspaceHome.history.back')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowLeft size={HEADER_ICON_SIZE} />}
                    onClick={startNewChat}
                    aria-label={t('workspaceHome.history.back')}
                  />
                </Tooltip>
                <ChatHistory
                  squad={squad}
                  open={historyOpen && popoverTarget === 'conversation'}
                  onOpenChange={(open) =>
                    onPopoverOpenChange('conversation', open)
                  }
                  trigger={
                    <button
                      type="button"
                      className={styles.conversationSwitchButton}
                    >
                      <Text
                        strong
                        ellipsis
                        className={styles.conversationSwitchTitle}
                      >
                        {active.title}
                      </Text>
                      <ChevronDown
                        size={14}
                        className={styles.headerSelectorIcon}
                      />
                    </button>
                  }
                />
              </Flex>

              <Flex align="center" gap={4}>
                <ChatFilesButton
                  threadId={isTempId(active.id) ? null : active.id}
                  appToken={appToken ?? ''}
                />
                <Tooltip title={t('workspaceHome.history.newChat')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<SquarePen size={HEADER_ICON_SIZE} />}
                    onClick={startNewChat}
                    disabled={session.streaming}
                    aria-label={t('workspaceHome.history.newChat')}
                  />
                </Tooltip>
                <ChatHistory
                  squad={squad}
                  open={historyOpen && popoverTarget === 'history'}
                  onOpenChange={(open) => onPopoverOpenChange('history', open)}
                  trigger={
                    <Tooltip title={t('workspaceHome.history.title')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<History size={HEADER_ICON_SIZE} />}
                        aria-label={t('workspaceHome.history.title')}
                      />
                    </Tooltip>
                  }
                />
              </Flex>
            </Flex>

            <SquadChatConversation
              conversation={active}
              input={input}
              onChange={setInput}
              onSend={sendComposer}
              onStop={session.stop}
              streaming={session.streaming}
              disabled={!configured || awaitingResolution}
              composerExtras={composerExtras}
              resolveAttachmentUrl={files.resolveUrl}
              loadingHistory={conversations.loadingActive}
              appToken={appToken ?? ''}
              aiIcon={<AgentGlyph agent={glyphAgent} size={AI_ICON_SIZE} />}
              hideTitle
              onApprovalResolve={session.resolveApproval}
              onQuestionResolve={session.resolveQuestion}
            />
          </Card>
        ) : (
          <ChatWelcome
            ref={welcomeRef}
            greeting={greeting}
            firstName={firstName}
            avatar={
              <AgentGlyph
                agent={glyphAgent}
                size={HERO_AVATAR_SIZE}
                className={styles.welcomeAvatarPlate}
              />
            }
            role={suggestionRole}
            input={input}
            onChange={setInput}
            onSend={sendComposer}
            onStop={session.stop}
            streaming={session.streaming}
            disabled={!configured}
            onPickSuggestion={submit}
            composerExtras={composerExtras}
          />
        )}
      </div>
    </div>
  )
}
